import {
  createCorePreferencesPort,
  PreferencesBridge,
  type PreferencesOwner,
  PreferencesProvider,
} from '@frontends/util-preferences';
import { PropsWithChildren, useMemo } from 'react';
import { useLocation } from 'react-router';
import { AppearanceQueryCleanup } from '@/components/AppearanceQueryCleanup';
import useAuth from '@/hooks/useAuth';
import useAxiosInterceptors from '@/hooks/useAxiosInterceptors';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import { crmOriginOf, isEmbedPath, isEmbedSession } from '@/utils/b2coreEmbed';

export const PreferencesGateway = ({ children }: PropsWithChildren) => {
  const config = useEntityConfig();
  const { client } = useAuth();
  const axiosPrivate = useAxiosInterceptors();
  const { pathname } = useLocation();

  const port = useMemo(
    () =>
      createCorePreferencesPort({
        baseUrl: config.VITE_URL_CORE,
        http: {
          get: (url, cfg) => axiosPrivate.get(url, cfg).then((response) => response.data),
          put: (url, body) => axiosPrivate.put(url, body).then((response) => response.data),
          patch: (url, body) => axiosPrivate.patch(url, body).then((response) => response.data),
        },
      }),
    [config.VITE_URL_CORE, axiosPrivate],
  );

  const clientId = client?.client_details?.id ?? null;

  const owner = useMemo<PreferencesOwner | null>(
    () => (clientId === null || !config.VITE_URL_CORE ? null : { kind: 'external', id: String(clientId) }),
    [clientId, config.VITE_URL_CORE],
  );

  return (
    <PreferencesProvider
      owner={owner}
      port={port}
      persist={!(isEmbedPath(pathname) || isEmbedSession(crmOriginOf(config.VITE_CLIENT_PORTAL_URL)))}
      stripQueryParams={false}
      announceTo={[config.VITE_TRADING_VIEW_URL, config.VITE_TRADING_VIEW_REPLAY_URL]}
    >
      <PreferencesBridge offeredLanguages={config.VITE_CLIENT_PORTAL_SUPPORTED_LOCALES}>
        <AppearanceQueryCleanup />
        {children}
      </PreferencesBridge>
    </PreferencesProvider>
  );
};
